import { createListenerMiddleware } from '@reduxjs/toolkit';
import { logger } from 'common/services';
import { addAppErrorMessage } from 'modules/alerts/state';
import { keycloakService } from 'modules/keycloak/services';
import { selectUserAccountId } from 'user/api';

import { openNatterApi } from '../api/openNatterApi';
import { getOpenNatterChannels } from '../constants/channels';
import { JOIN_RETRY_CONFIG, MARK_READY_CONFIG } from '../constants/websocket';
import { openNatterFeature } from '../OpenNatterFeature';
import { selfLeftEventAction, selfMatchedAction, selfUnmatchedAction } from '../socket/openNatterMessageDispatcher';
import { sequenceChecker } from '../socket/sequenceChecker';
import { OpenNatterPhase } from '../types/state';
import { natterActions } from './openNatter.actions';
import {
  countdownEnded,
  leaveOpenNatter,
  markReady,
  socketReconnected,
  subscribeToOpenNatter,
  unsubscribeFromOpenNatter,
} from './openNatter.actions';
import { selectOpenNatterId, selectOpenNatterPhase } from './openNatter.selectors';
import { markReadyTimeout } from './openNatter.slice';

import type { ListenerEffectAPI, TypedStartListening } from '@reduxjs/toolkit';
import type { CombinedDispatch, RootState } from 'store';

/**
 * Type definitions for listener middleware
 */
export type AppStartListening = TypedStartListening<RootState, CombinedDispatch>;

export const openNatterListener = createListenerMiddleware();

const startAppListening = openNatterListener.startListening as AppStartListening;

/**
 * Helper function to wait for phase change with timeout
 * Used to handle race conditions between API calls and WebSocket messages
 */
const waitForPhaseChange = async (
  listenerApi: ListenerEffectAPI<RootState, CombinedDispatch>,
  fromPhase: OpenNatterPhase,
  timeoutMs: number = MARK_READY_CONFIG.TIMEOUT_MS
): Promise<void> => {
  const timeout = new Promise<void>((_, reject) => {
    setTimeout(() => {
      const currentPhase = selectOpenNatterPhase(listenerApi.getState());
      if (currentPhase === fromPhase) {
        reject(new Error(`Phase did not change from ${fromPhase} after ${timeoutMs}ms`));
      }
    }, timeoutMs);
  });

  const phaseChange = listenerApi.condition((_action, currentState, previousState) => {
    const prevPhase = selectOpenNatterPhase(previousState);
    const currPhase = selectOpenNatterPhase(currentState);
    return prevPhase === fromPhase && currPhase !== fromPhase;
  });

  await Promise.race([phaseChange, timeout]);
};

/**
 * Track page-unload handler so we can clean it up on unsubscribe
 */
let pagehideHandler: (() => void) | null = null;

/**
 * The natter id + channel names we actually subscribed to, captured at subscribe time. Reused
 * verbatim at unsubscribe/cleanup so we detach exactly what we attached (never re-derived from a
 * userId that could momentarily be undefined). The id is tracked so cleanup for a FAILED join can't
 * tear down a different, still-active session's channels.
 */
let activeSubscription: { id: string; channels: { personal: string; broadcast: string } } | null = null;

/**
 * Set when the user explicitly leaves, cleared on a fresh subscribe. Gates reconnect re-join so a
 * reconnect that lands before the `SelfLeftEvent` arrives can't re-join a user who just left.
 */
let hasExplicitlyLeft = false;

/**
 * Listener: Join + subscribe to Ably channels
 * Creates/reuses socket for the given natter+user, connects if needed, subscribes.
 * After subscribing, dispatches postJoin so the backend emits self-state.
 */
startAppListening({
  actionCreator: subscribeToOpenNatter,
  effect: async (action, listenerApi) => {
    const { id } = action.payload;
    // Fresh join intent — clear any prior "explicitly left" flag so reconnect re-join works again.
    hasExplicitlyLeft = false;

    try {
      // The Ably clientId + personal channel must use the backend's numeric account id (the
      // gateway authorizer derives X-User-Id from account.id, NOT the Keycloak sub), or the
      // client subscribes to an empty personal channel and never receives self-state.
      const userId = selectUserAccountId(listenerApi.getState())?.toString();
      if (!userId) {
        logger.error('[OpenNatter Listener] No account id available; cannot join', { id });
        listenerApi.dispatch(addAppErrorMessage('Unable to connect to Open Natter. Please try again.'));
        return;
      }
      // 1. JOIN FIRST. The `ably-token` endpoint only mints a token for an ACTIVE member, so the
      //    domain join must happen before we connect Ably — otherwise the token request 403s and the
      //    connection never establishes. `.unwrap()` so a failed join surfaces an error (RTK Query
      //    mutations resolve even on error) instead of leaving the user on a silent join spinner.
      await listenerApi.dispatch(openNatterApi.endpoints.postJoin.initiate(id)).unwrap();

      // 2. THEN connect Ably + subscribe. The token authCallback now succeeds, and the personal
      //    channel's rewind=1 replays the SelfJoinedWaitingRoom snapshot published during the join.
      const channels = getOpenNatterChannels(id, userId);
      activeSubscription = { id, channels };

      const socket = openNatterFeature.getSocket(id, userId);
      if (!socket.isConnected()) {
        socket.connect();
      }
      socket.subscribe(channels);

      logger.info('[OpenNatter Listener] Joined + subscribed to channels', { id, channels });

      // Register page-unload handler for best-effort disconnect notification
      if (pagehideHandler) {
        window.removeEventListener('pagehide', pagehideHandler);
        window.removeEventListener('beforeunload', pagehideHandler);
      }

      const apiBaseUrl = openNatterFeature.getApiUrl();
      // `pagehide` AND `beforeunload` both fire on a normal close/navigation, so the handler must
      // run exactly once: it detaches both listeners on first invocation before disconnecting.
      const unloadHandler = () => {
        window.removeEventListener('pagehide', unloadHandler);
        window.removeEventListener('beforeunload', unloadHandler);
        pagehideHandler = null;
        socket.disconnect();
        void fetch(`${apiBaseUrl}/open-natters/${id}/disconnect`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${keycloakService.token ?? ''}` },
          keepalive: true,
        });
      };
      pagehideHandler = unloadHandler;
      window.addEventListener('pagehide', pagehideHandler);
      window.addEventListener('beforeunload', pagehideHandler);
    } catch (error) {
      logger.error('[OpenNatter Listener] Failed to subscribe to channels', { error, id });
      // Tear down any channels we attached for THIS session before failing, so a retry doesn't stack
      // a second anonymous listener (double dispatch). Id-scoped so a failed join can't unsubscribe a
      // different, still-active session's channels.
      if (activeSubscription?.id === id) {
        openNatterFeature.getSocket().unsubscribe(activeSubscription.channels);
        activeSubscription = null;
      }
      listenerApi.dispatch(
        addAppErrorMessage('Unable to connect to Open Natter. Please check your connection and try again.')
      );
    }
  },
});

/**
 * Listener: Unsubscribe from Ably channels
 * Note: Does NOT reset state - that should happen when starting a new session
 * This allows the Ended phase to persist for the rating screen
 */
startAppListening({
  actionCreator: unsubscribeFromOpenNatter,
  effect: async (action, _listenerApi) => {
    const { id } = action.payload;

    try {
      // Use the exact channels captured at subscribe time (for this id) so we detach precisely what
      // we attached. Fall back to deriving from the account id only if we never recorded them.
      const channels =
        activeSubscription?.id === id
          ? activeSubscription.channels
          : getOpenNatterChannels(id, selectUserAccountId(_listenerApi.getState())?.toString() ?? '');
      if (activeSubscription?.id === id) {
        activeSubscription = null;
      }
      const socket = openNatterFeature.getSocket();

      // Unsubscribe from channels
      socket.unsubscribe(channels);

      // Clear sequence tracking for this event (prevents stale data on rejoin)
      sequenceChecker.reset(id);

      // Remove page-unload handler
      if (pagehideHandler) {
        window.removeEventListener('pagehide', pagehideHandler);
        window.removeEventListener('beforeunload', pagehideHandler);
        pagehideHandler = null;
      }

      logger.info('[OpenNatter Listener] Unsubscribed from channels', { id, channels });
    } catch (error) {
      logger.error('[OpenNatter Listener] Failed to unsubscribe from channels', { error, id });
    }
  },
});

/**
 * Listener: socketReconnected (fires ONLY on reconnect, never the initial connect)
 * Re-dispatches postJoin if the user was already in an active natter session, so the backend
 * re-emits self-state after reconnection. Gated to reconnect specifically so it can't double-join
 * on the initial connect (where subscribeToOpenNatter already issues the join), even if a stale
 * non-Idle phase is persisted from a prior session.
 */
startAppListening({
  actionCreator: socketReconnected,
  effect: async (_action, listenerApi) => {
    const state = listenerApi.getState() as RootState;
    const phase = selectOpenNatterPhase(state);
    const id = selectOpenNatterId(state);

    // Only re-join if we have an active natter, aren't Idle/Ended, and the user hasn't just left.
    // (`hasExplicitlyLeft` covers the race where a reconnect lands before SelfLeftEvent flips the
    // phase to Ended — without it we'd re-join a user who clicked leave.)
    if (id && phase !== OpenNatterPhase.Idle && phase !== OpenNatterPhase.Ended && !hasExplicitlyLeft) {
      logger.info('[OpenNatter Listener] Reconnected - re-dispatching postJoin', { id, phase });
      try {
        await listenerApi.dispatch(openNatterApi.endpoints.postJoin.initiate(id)).unwrap();
      } catch (error) {
        // Non-fatal: rewind already repainted optimistically; log and let the next reconnect retry.
        logger.warn('[OpenNatter Listener] Re-join on reconnect failed', { error, id });
      }
    }
  },
});

/**
 * Listener: Join Open Natter with timeout and retry logic
 * Waits for the phase to change from Idle after subscribing to Ably channels
 * Retries up to 3 times with progressive timeouts: 5s, 10s, 15s
 */
startAppListening({
  actionCreator: natterActions.joinNatterClicked.fulfilled,
  effect: async (action, listenerApi) => {
    for (let attempt = 1; attempt <= JOIN_RETRY_CONFIG.MAX_ATTEMPTS; attempt++) {
      const timeoutMs = JOIN_RETRY_CONFIG.BASE_TIMEOUT_MS * attempt; // 5s, 10s, 15s

      try {
        logger.info('[OpenNatter Listener] Waiting for join snapshot', { attempt, timeoutMs });

        // Wait for phase to change from Idle (triggered by SelfJoinedWaitingRoom or other snapshot message)
        await waitForPhaseChange(listenerApi, OpenNatterPhase.Idle, timeoutMs);

        logger.info('[OpenNatter Listener] Successfully joined Open Natter - snapshot received', { attempt });
        return; // Success - exit the retry loop
      } catch (error) {
        logger.info('[OpenNatter Listener] Timeout waiting for join snapshot', { attempt, timeoutMs, error });

        // Check if phase changed during this attempt (race condition - success)
        const currentPhase = selectOpenNatterPhase(listenerApi.getState() as RootState);
        if (currentPhase !== OpenNatterPhase.Idle) {
          logger.info('[OpenNatter Listener] Phase changed during timeout - join successful', {
            attempt,
            currentPhase,
          });
          return; // Success - phase changed
        }

        if (attempt < JOIN_RETRY_CONFIG.MAX_ATTEMPTS) {
          // Retry: unsubscribe and resubscribe
          const id = selectOpenNatterId(listenerApi.getState() as RootState);
          if (id) {
            logger.info('[OpenNatter Listener] Retrying join - unsubscribe/resubscribe', {
              attempt,
              nextAttempt: attempt + 1,
            });
            listenerApi.dispatch(unsubscribeFromOpenNatter({ id }));

            // Small delay before resubscribing
            await listenerApi.delay(JOIN_RETRY_CONFIG.RETRY_DELAY_MS);

            listenerApi.dispatch(subscribeToOpenNatter({ id }));
          }
        }
      }
    }

    // All attempts failed
    logger.error('[OpenNatter Listener] All join attempts failed after retries');
    listenerApi.dispatch(
      addAppErrorMessage('Connection timed out while joining Open Natter. Please refresh and try again.')
    );

    // Unsubscribe on final failure
    const id = selectOpenNatterId(listenerApi.getState() as RootState);
    if (id) {
      listenerApi.dispatch(unsubscribeFromOpenNatter({ id }));
    }
  },
});

/**
 * Listener: Mark ready with timeout for phase change
 * Waits for the phase to change from Waiting after the API call
 * Timeout: 10 seconds - shows error and resets loading state
 */
startAppListening({
  actionCreator: markReady.fulfilled,
  effect: async (action, listenerApi) => {
    try {
      // Wait for phase to change from Waiting (triggered by SelfJoinedWaitingRoom with ready=true)
      await waitForPhaseChange(listenerApi, OpenNatterPhase.Waiting, MARK_READY_CONFIG.TIMEOUT_MS);

      logger.info('[OpenNatter Listener] Successfully marked ready');
    } catch (error) {
      logger.error('[OpenNatter Listener] Timeout waiting for ready confirmation', { error });

      // Reset loading state and show error - user can try again
      listenerApi.dispatch(markReadyTimeout());
      listenerApi.dispatch(addAppErrorMessage('Ready status not confirmed. Please try again.'));
    }
  },
});

/**
 * Listener: Leave Open Natter API call completed
 * Logs successful leave - unsubscription is handled by SelfLeftEvent listener
 */
startAppListening({
  actionCreator: leaveOpenNatter.fulfilled,
  effect: async (action, _listenerApi) => {
    const { id } = action.payload;
    // Record explicit leave so an in-flight reconnect can't re-join before SelfLeftEvent arrives.
    hasExplicitlyLeft = true;
    logger.info('[OpenNatter Listener] Successfully left Open Natter', { id });
  },
});

/**
 * Listener: SelfLeftEvent received
 * Unsubscribes from channels when natter ends (regardless of how it ended)
 * This handles both user-initiated leave and server-initiated end (timeout)
 */
startAppListening({
  actionCreator: selfLeftEventAction,
  effect: async (action, listenerApi) => {
    const id = action.payload.openNatterId.value;

    // Unsubscribe from channels
    listenerApi.dispatch(unsubscribeFromOpenNatter({ id }));

    logger.info('[OpenNatter Listener] SelfLeftEvent received - unsubscribed from channels', { id });
  },
});

/**
 * Listener: Countdown ended
 * Handles when the UI countdown timer reaches zero
 */
startAppListening({
  actionCreator: countdownEnded,
  effect: async (_action, listenerApi) => {
    const state = listenerApi.getState() as RootState;

    // Transition to appropriate phase based on starting soon state
    if (state.openNatter.startingSoon === 'matched') {
      // Already should be in Matched phase from SelfMatched message
      logger.info('[OpenNatter Listener] Countdown ended for matched room');
    } else if (state.openNatter.startingSoon === 'unmatched') {
      // Already should be in Unmatched phase from SelfUnmatched message
      logger.info('[OpenNatter Listener] Countdown ended for unmatched session');
    }
  },
});

/**
 * Listener: SelfMatched - wait for startsAt before transitioning to room
 * Dispatches userMatched thunk which waits until startsAt time
 * Uses serverNow to calculate delay independent of client clock skew
 * If startsAt is in the past (rejoin), the slice already set the phase, so we skip
 */
startAppListening({
  actionCreator: selfMatchedAction,
  effect: async (action, listenerApi) => {
    const { startsAt, serverNow } = action.payload;
    const startsAtTime = new Date(startsAt).getTime();
    const serverNowTime = new Date(serverNow).getTime();

    // If startsAt is in the past (server time), the slice already transitioned the phase
    if (startsAtTime <= serverNowTime) {
      logger.info('[OpenNatter Listener] Matched - startsAt already passed, phase set by reducer');
      return;
    }

    const delayMs = startsAtTime - serverNowTime;
    logger.info('[OpenNatter Listener] Matched - waiting for room start', { startsAt, serverNow, delayMs });

    // Wait until startsAt before transitioning phase to Matched (using server time)
    await listenerApi.dispatch(natterActions.userMatched({ startsAt, serverNow })).unwrap();

    logger.info('[OpenNatter Listener] Room start time reached, transitioning to Matched');
  },
});

/**
 * Listener: SelfUnmatched - wait for startsAt before transitioning to unmatched session
 * Dispatches userUnmatched thunk which waits until startsAt time
 * Uses serverNow to calculate delay independent of client clock skew
 * If startsAt is in the past (rejoin), the slice already set the phase, so we skip
 */
startAppListening({
  actionCreator: selfUnmatchedAction,
  effect: async (action, listenerApi) => {
    const { startsAt, serverNow } = action.payload;
    const startsAtTime = new Date(startsAt).getTime();
    const serverNowTime = new Date(serverNow).getTime();

    // If startsAt is in the past (server time), the slice already transitioned the phase
    if (startsAtTime <= serverNowTime) {
      logger.info('[OpenNatter Listener] Unmatched - startsAt already passed, phase set by reducer');
      return;
    }

    const delayMs = startsAtTime - serverNowTime;
    logger.info('[OpenNatter Listener] Unmatched - waiting for session start', { startsAt, serverNow, delayMs });

    // Wait until startsAt before transitioning phase to Unmatched (using server time)
    await listenerApi.dispatch(natterActions.userUnmatched({ startsAt, serverNow })).unwrap();

    logger.info('[OpenNatter Listener] Session start time reached, transitioning to Unmatched');
  },
});
